import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import ViewListIcon from '@mui/icons-material/ViewList'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InsightsIcon from '@mui/icons-material/Insights'
import EventIcon from '@mui/icons-material/Event'
import HistoryIcon from '@mui/icons-material/History'
import BusinessIcon from '@mui/icons-material/Business'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import API from '../../services/API'
import { CRMContext } from '../CRM'
import OwnerAvatar from '../kit/OwnerAvatar'
import PageHeader from '../kit/PageHeader'
import StatTile from '../kit/StatTile'
import ConfirmDialog from '../kit/ConfirmDialog'
import dataGridSx from '../kit/dataGridSx'
import { DemoChip, InterestChip, SoftChip, StageChip, TrialledChip } from '../kit/Chips'
import {
  LEGACY_STAGE,
  STAGES,
  followUpState,
  formatDate,
  fromNow,
  isOpenStage,
  tenantUrl,
} from '../kit/helpers'
import PipelineBoard from './PipelineBoard'
import LeadDialog from './LeadDialog'
import LinkLeadDialog from './LinkLeadDialog'

const isDue = (row) => {
  const state = followUpState(row.nextFollowUpAt)
  return state && state.key !== 'upcoming'
}

const FILTERS = [
  {
    key: 'open',
    label: 'Open pipeline',
    predicate: (row) => isOpenStage(row.stage),
    stages: ['New', 'Contacted', 'Engaged'],
  },
  {
    key: 'due',
    label: 'Follow-up due',
    predicate: (row) => isOpenStage(row.stage) && isDue(row),
    stages: ['New', 'Contacted', 'Engaged'],
  },
  {
    key: 'mailing',
    label: 'Mailing list',
    predicate: (row) => row.mailingList,
    stages: ['New', 'Contacted', 'Engaged', 'Converted', 'Lost'],
  },
  {
    key: 'customers',
    label: 'Customers',
    predicate: (row) => row.stage === 'Converted',
    stages: ['Converted'],
  },
  {
    key: 'lost',
    label: 'Lost',
    predicate: (row) => row.stage === 'Lost',
    stages: ['Lost'],
  },
  {
    key: 'all',
    label: 'Everything',
    predicate: () => true,
    stages: ['New', 'Contacted', 'Engaged', 'Converted', 'Lost'],
  },
]

const VIEW_STORAGE_KEY = 'crm.pipeline.view'
const SCOPE_STORAGE_KEY = 'crm.pipeline.scope'

const readStored = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback
  } catch (e) {
    return fallback
  }
}

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // localStorage unavailable - the toggle still works for this visit.
  }
}

const isOwnedBy = (row, userId) => Boolean(userId) && String(row.owner?._id) === String(userId)

const Pipeline = ({ openNew = false }) => {
  const navigate = useNavigate()
  const { user } = useContext(CRMContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [view, setView] = useState(() => searchParams.get('view') || readStored(VIEW_STORAGE_KEY, 'board'))
  // All leads vs only the ones the signed-in CRM user owns. Remembered per
  // browser so "My leads" sticks for whoever prefers it.
  const [scope, setScope] = useState(() =>
    (searchParams.get('owner') || readStored(SCOPE_STORAGE_KEY, 'all')) === 'mine' ? 'mine' : 'all',
  )
  const [newLeadOpen, setNewLeadOpen] = useState(openNew)
  const [menu, setMenu] = useState(null) // { anchor, row }
  const [lostTarget, setLostTarget] = useState(null)
  const [linkTarget, setLinkTarget] = useState(null) // tenant-only row
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const filterKey = searchParams.get('filter') || 'open'
  const filter = FILTERS.find((option) => option.key === filterKey) ?? FILTERS[0]

  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'open') next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  const changeView = (next) => {
    if (!next) return
    setView(next)
    writeStored(VIEW_STORAGE_KEY, next)
  }

  const changeScope = (next) => {
    setScope(next)
    writeStored(SCOPE_STORAGE_KEY, next)
    const params = new URLSearchParams(searchParams)
    if (next === 'mine') params.set('owner', 'mine')
    else params.delete('owner')
    setSearchParams(params, { replace: true })
  }

  useEffect(() => {
    setNewLeadOpen(openNew)
  }, [openNew])

  const load = () => {
    setLoading(true)
    API.get('/admincrm/sales/pipeline')
      .then((res) => {
        if (res.status === 200) setRows(res.data)
        else setError(res.data?.message || 'Could not load the pipeline.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const scopedRows = useMemo(
    () => (scope === 'mine' ? rows.filter((row) => isOwnedBy(row, user?._id)) : rows),
    [rows, scope, user?._id],
  )

  const scopeCounts = useMemo(
    () => ({
      all: rows.filter(filter.predicate).length,
      mine: rows.filter((row) => filter.predicate(row) && isOwnedBy(row, user?._id)).length,
    }),
    [rows, filter, user?._id],
  )

  const stats = useMemo(() => {
    // Look filters up by key - positional indexes broke once when FILTERS was reordered.
    const countBy = (key) => scopedRows.filter(FILTERS.find((option) => option.key === key).predicate).length
    return {
      open: countBy('open'),
      due: countBy('due'),
      customers: countBy('customers'),
    }
  }, [scopedRows])

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return scopedRows
      .filter(filter.predicate)
      .filter((row) => {
        if (!query) return true
        return [
          row.organisationName,
          row.tenant?.subdomain,
          row.contactName,
          row.contactEmail,
          row.contactPhone,
          row.source,
          row.stage,
          row.notes,
          row.owner?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query)
      })
      .sort((a, b) => {
        // Due follow-ups first, then most recently touched.
        const aDue = isDue(a) ? 0 : 1
        const bDue = isDue(b) ? 0 : 1
        if (aDue !== bDue) return aDue - bDue
        return new Date(b.updatedAt ?? b.createdAt) - new Date(a.updatedAt ?? a.createdAt)
      })
  }, [scopedRows, filter, search])

  const boardStages = useMemo(() => {
    const stages = [...filter.stages]
    if (visibleRows.some((row) => row.stage === LEGACY_STAGE.key)) {
      const index = stages.indexOf('Engaged')
      stages.splice(index === -1 ? stages.length : index + 1, 0, LEGACY_STAGE.key)
    }
    return stages
  }, [filter, visibleRows])

  const patchRow = (id, patch) =>
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  // Opening a tenant-only row turns it into a real lead first, so it can be
  // worked like any other.
  const openRow = async (row) => {
    if (row.leadId) {
      navigate(`/sales/${row.leadId}`)
      return
    }
    setBusy(true)
    const res = await API.post('/admincrm/sales/tenant-lead', { tenantId: row.tenantId })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not open this organisation.')
      return
    }
    navigate(`/sales/${res.data._id}`)
  }

  const moveStage = async (row, stage) => {
    if (stage === 'Lost') {
      setLostTarget(row)
      return
    }
    const previous = row.stage
    patchRow(row.id, { stage })
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/customer-stage', {
      leadId: row.leadId ?? undefined,
      tenantId: row.tenantId ?? undefined,
      stage,
    })
    setBusy(false)
    if (res.status !== 200) {
      patchRow(row.id, { stage: previous })
      setError(res.data?.message || 'Could not update the stage.')
      return
    }
    patchRow(row.id, {
      stage: res.data.stage,
      leadId: res.data._id,
      tenant:
        row.tenant && res.data.tenantIsActive !== undefined
          ? { ...row.tenant, isActive: res.data.tenantIsActive }
          : row.tenant,
    })
  }

  const confirmLost = async () => {
    if (!lostTarget) return
    setBusy(true)
    setError('')
    const res = lostTarget.tenantId
      ? await API.post('/admincrm/sales/tenant-lost', { tenantId: lostTarget.tenantId })
      : await API.post('/admincrm/sales/customer-stage', { leadId: lostTarget.leadId, stage: 'Lost' })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not mark this organisation as lost.')
      return
    }
    const lead = res.data.lead ?? res.data
    patchRow(lostTarget.id, {
      stage: 'Lost',
      leadId: lead?._id ?? lostTarget.leadId,
      tenant: lostTarget.tenant ? { ...lostTarget.tenant, isActive: false } : null,
    })
    setLostTarget(null)
  }

  const archiveRow = async (row) => {
    if (!row.leadId) return
    setBusy(true)
    const res = await API.post(`/admincrm/sales/leads/${row.leadId}/archive`, { archived: true })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not archive this lead.')
      return
    }
    setRows((prev) => prev.filter((item) => item.id !== row.id))
  }

  const openMenu = (event, row) => setMenu({ anchor: event.currentTarget, row })
  const closeMenu = () => setMenu(null)

  const columns = [
    {
      field: 'organisationName',
      headerName: 'Organisation',
      minWidth: 240,
      flex: 1.2,
      renderCell: ({ row }) => (
        <Stack minWidth={0} py={0.5}>
          <Typography fontWeight={700} noWrap>
            {row.organisationName}
          </Typography>
          <Stack direction="row" gap={0.5} alignItems="center" minWidth={0}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.tenant?.subdomain ? `${row.tenant.subdomain}.${process.env.ROOT_DOMAIN}` : 'Lead only'}
            </Typography>
            {row.demoAt && <DemoChip demoAt={row.demoAt} sx={{ height: 18, fontSize: 10 }} />}
            {row.trialled && <TrialledChip trialEnd={row.trialEnd} sx={{ height: 18, fontSize: 10 }} />}
            {row.tenant && row.tenant.isActive === false && (
              <SoftChip color="#E32525" label="Disabled" sx={{ height: 18, fontSize: 10 }} />
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      minWidth: 200,
      flex: 0.9,
      renderCell: ({ row }) => (
        <Stack minWidth={0}>
          <Typography variant="body2" color="text.primary" noWrap>
            {row.contactName || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.contactEmail || row.contactPhone || ''}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'stage',
      headerName: 'Stage',
      minWidth: 120,
      flex: 0.4,
      renderCell: ({ value }) => <StageChip stage={value} />,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      minWidth: 160,
      flex: 0.6,
      valueGetter: (value) => value?.name ?? '',
      renderCell: ({ row }) => (
        <OwnerAvatar owner={row.owner} showName isMe={isOwnedBy(row, user?._id)} />
      ),
    },
    {
      field: 'interestLevel',
      headerName: 'Interest',
      minWidth: 130,
      flex: 0.4,
      renderCell: ({ value }) => <InterestChip level={value} />,
    },
    {
      field: 'source',
      headerName: 'Source',
      minWidth: 130,
      flex: 0.5,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {value || '—'}
        </Typography>
      ),
    },
    {
      field: 'nextFollowUpAt',
      headerName: 'Follow-up',
      minWidth: 140,
      flex: 0.5,
      valueGetter: (value) => (value ? new Date(value) : null),
      sortComparator: (a, b) => (a?.getTime() ?? Infinity) - (b?.getTime() ?? Infinity),
      renderCell: ({ value }) => {
        const state = followUpState(value)
        if (!state) return <Typography variant="body2" color="text.disabled">—</Typography>
        return (
          <Typography variant="body2" fontWeight={700} sx={{ color: state.color }}>
            {state.key === 'upcoming' ? formatDate(value) : state.label}
          </Typography>
        )
      },
    },
    {
      field: 'lastContactedAt',
      headerName: 'Last contact',
      minWidth: 130,
      flex: 0.45,
      valueGetter: (value) => (value ? new Date(value) : null),
      sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
      renderCell: ({ value }) => (
        <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
          {value ? fromNow(value) : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Added',
      minWidth: 120,
      flex: 0.4,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <IconButton size="small" aria-label="Actions" onClick={(event) => {
          event.stopPropagation()
          openMenu(event, row)
        }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  const menuRow = menu?.row
  const stageOptions = STAGES.filter((stage) => stage.key !== menuRow?.stage)

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Sales"
        title="Pipeline"
        subtitle="Every lead and organisation, from first contact to paying customer."
        actions={
          <>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(event, next) => changeView(next)}
              sx={{ bgcolor: '#FFFFFF' }}
            >
              <ToggleButton value="board" aria-label="Board view">
                <Tooltip title="Board">
                  <ViewKanbanIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                <Tooltip title="List">
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewLeadOpen(true)}>
              New lead
            </Button>
          </>
        }
      />

      <Box sx={{ borderBottom: '1px solid #E2E8F0' }}>
        <Tabs
          value={scope}
          onChange={(event, value) => changeScope(value)}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 }, minHeight: 44 }}
        >
          <Tab value="all" label={`All leads (${scopeCounts.all})`} />
          <Tab value="mine" label={`My leads (${scopeCounts.mine})`} />
        </Tabs>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Open leads"
            value={stats.open}
            caption="New, contacted or engaged"
            icon={<InsightsIcon />}
            tone="#268EBB"
            active={filterKey === 'open'}
            onClick={() => setFilter('open')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Follow-up due"
            value={stats.due}
            caption="Due today or overdue"
            icon={<EventIcon />}
            tone={stats.due ? '#E32525' : '#64748B'}
            active={filterKey === 'due'}
            onClick={() => setFilter('due')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Customers"
            value={stats.customers}
            caption="Paying subscriber stage"
            icon={<BusinessIcon />}
            tone="#00964C"
            active={filterKey === 'customers'}
            onClick={() => setFilter('customers')}
          />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" gap={0.75} flexWrap="wrap" flex={1}>
          {FILTERS.map((option) => (
            <Chip
              key={option.key}
              label={option.label}
              clickable
              onClick={() => setFilter(option.key)}
              color={filterKey === option.key ? 'primary' : 'default'}
              variant={filterKey === option.key ? 'filled' : 'outlined'}
              sx={{ borderRadius: 2, fontWeight: 600, bgcolor: filterKey === option.key ? undefined : '#FFFFFF' }}
            />
          ))}
        </Stack>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search organisations, contacts, sources…"
          size="small"
          sx={{ minWidth: { md: 320 }, bgcolor: '#FFFFFF' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {(loading || busy) && <LinearProgress sx={{ borderRadius: 1 }} />}

      {view === 'board' ? (
        <PipelineBoard
          stages={boardStages}
          rows={visibleRows}
          onOpen={openRow}
          onMenu={openMenu}
          onMoveStage={moveStage}
        />
      ) : (
        <DataGrid
          rows={visibleRows}
          columns={columns}
          autoHeight
          loading={loading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          onRowClick={({ row }) => openRow(row)}
          rowHeight={60}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={dataGridSx}
        />
      )}

      {!loading && visibleRows.length === 0 && view === 'board' && (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="text.secondary">
            {search
              ? 'Nothing matches that search.'
              : scope === 'mine'
                ? `You don't own any leads in "${filter.label}".`
                : `No organisations in "${filter.label}".`}
          </Typography>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu()
            openRow(menuRow)
          }}
        >
          {menuRow?.leadId ? 'Open lead' : 'Open as lead'}
        </MenuItem>
        {menuRow?.tenantId && !menuRow?.leadId && (
          <MenuItem
            onClick={() => {
              closeMenu()
              setLinkTarget(menuRow)
            }}
          >
            Link to existing lead
          </MenuItem>
        )}
        {menuRow?.tenantId && (
          <MenuItem
            onClick={() => {
              closeMenu()
              navigate(`/tenants/${menuRow.tenantId}`)
            }}
          >
            Open tenant record
          </MenuItem>
        )}
        {menuRow?.tenant?.subdomain && (
          <MenuItem
            onClick={() => {
              closeMenu()
              window.open(tenantUrl(menuRow.tenant.subdomain), '_blank')
            }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              Visit site <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Stack>
          </MenuItem>
        )}
        {menuRow?.contactEmail && (
          <MenuItem
            onClick={() => {
              closeMenu()
              window.location.href = `mailto:${menuRow.contactEmail}?subject=${encodeURIComponent(`AmbuNet - ${menuRow.organisationName}`)}`
            }}
          >
            Email {menuRow.contactName?.split(' ')[0] || 'contact'}
          </MenuItem>
        )}
        <Divider />
        <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>Move to</ListSubheader>
        {stageOptions.map((stage) => (
          <MenuItem
            key={stage.key}
            disabled={busy}
            onClick={() => {
              closeMenu()
              moveStage(menuRow, stage.key)
            }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stage.color }} />
              {stage.label}
              {stage.key === 'Lost' && menuRow?.tenantId ? ' (disables tenant)' : ''}
            </Stack>
          </MenuItem>
        ))}
        {menuRow?.leadId && !menuRow?.tenantId && (
          <>
            <Divider />
            <MenuItem
              disabled={busy}
              sx={{ color: 'error.main' }}
              onClick={() => {
                closeMenu()
                archiveRow(menuRow)
              }}
            >
              Archive lead
            </MenuItem>
          </>
        )}
      </Menu>

      <ConfirmDialog
        open={!!lostTarget}
        title={`Mark ${lostTarget?.organisationName} as lost`}
        confirmLabel={lostTarget?.tenantId ? 'Mark lost & disable tenant' : 'Mark lost'}
        confirmColor="error"
        busy={busy}
        error={error}
        onClose={() => setLostTarget(null)}
        onConfirm={confirmLost}
      >
        {lostTarget?.tenantId ? (
          <Alert severity="info">
            Their tenant will be disabled so nobody can log in. Nothing is deleted - moving them
            back into the pipeline re-enables it. Their user count and last activity are recorded
            against the lead first.
          </Alert>
        ) : (
          <Alert severity="info">This lead has no tenant, so nothing else changes.</Alert>
        )}
      </ConfirmDialog>

      <LinkLeadDialog
        open={!!linkTarget}
        tenant={linkTarget ? { _id: linkTarget.tenantId, name: linkTarget.organisationName } : null}
        onClose={() => setLinkTarget(null)}
        onLinked={(lead) => {
          setLinkTarget(null)
          // The tenant-only row is replaced by the lead's own row, so refetch
          // rather than guess at the merged shape.
          load()
          navigate(`/sales/${lead._id}`)
        }}
      />

      <LeadDialog
        open={newLeadOpen}
        onClose={() => {
          setNewLeadOpen(false)
          if (openNew) navigate('/sales', { replace: true })
        }}
        onCreated={(lead) => {
          setNewLeadOpen(false)
          navigate(`/sales/${lead._id}`)
        }}
      />
    </Stack>
  )
}

export default Pipeline
